import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function LoginPage() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (!email || !password) {
      setFormError("Please enter both email and password.");
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setFormError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#7C3AED]/20 via-[#0A0F1C] to-[#F59E0B]/20 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#7C3AED]/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#F59E0B]/20 rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/05veeg6r_Untitled%20%281920%20x%201080%20px%29%20%281920%20x%20750%20px%29%20%282%29.png"
              alt="LocalBest.ai"
              className="h-16 w-auto object-contain"
            />
          </Link>
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white font-['Outfit'] mb-4">
            Power Your Directory Presence
          </h1>
          <p className="text-xl text-[#94A3B8] mb-8">
            Manage your listing, publish content, showcase inventory, and capture leads — all from one powerful dashboard.
          </p>
          <div className="flex flex-col gap-3">
            {[
              "Complete listing management",
              "Content publishing system",
              "Inventory & marketplace tools",
              "Lead capture & tracking",
              "Retargeting pixel integration",
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-[#E2E8F0]">
                <div className="w-5 h-5 rounded-full bg-[#7C3AED]/30 flex items-center justify-center">
                  <ArrowRight className="w-3 h-3 text-[#7C3AED]" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[#64748B] text-sm">
          © 2026 LocalBest.ai. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/05veeg6r_Untitled%20%281920%20x%201080%20px%29%20%281920%20x%20750%20px%29%20%282%29.png"
                alt="LocalBest.ai"
                className="h-12 object-contain"
              />
            </Link>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-2">
              Welcome Back
            </h2>
            <p className="text-[#94A3B8]">
              Sign in to access your dashboard
            </p>
          </div>

          {/* Error Message */}
          {(formError || error) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg"
            >
              <p className="text-[#EF4444] text-sm">{formError || error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[#E2E8F0]">
                  Password
                </label>
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-[#7C3AED] hover:text-[#8B5CF6] transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-11 pr-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#8B5CF6] hover:to-[#F472B6] text-white font-semibold text-base"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-[#94A3B8]">
              Don't have an account?{" "}
              <Link
                to="/?create=true"
                className="text-[#7C3AED] hover:text-[#8B5CF6] font-medium transition-colors"
              >
                Create My Free Listing
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/?create=true"
              className="text-sm text-[#F59E0B] hover:text-[#FBBF24] transition-colors"
            >
              Already have a listing? Claim it here
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
